<template>
  <div class="ibox">
    <div class="ibox-title d-flex justify-content-between align-items-center">
      <span>WEBHOOKS</span>

      <button v-if="hasAvailableEvents" class="btn btn-primary btn-sm" @click="openForm()">
        Cadastrar webhook
      </button>
    </div>

    <div class="ibox-content">
      <div class="row mb-3">
        <div class="col-md-12">
          <p class="mb-0">
            Avisamos o seu sistema por <strong>POST</strong> quando um evento acontece na conta.
            Cada requisição vai assinada no header <strong>X-Brobot-Signature</strong>.
          </p>
        </div>
      </div>

      <div v-if="!hasAvailableEvents" class="row">
        <div class="col-md-12">
          <p>Nenhum evento disponível para os aplicativos ativos na sua conta.</p>
        </div>
      </div>

      <template v-else>
        <div class="row">
          <div class="col-md-12">
            <div class="wrap-table">
              <table class="table table-borderless table-border table-align table-h51">
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Eventos</th>
                    <th>XML no envio</th>
                    <th>Situação</th>
                    <th>Criado em</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="webhook in webhooks" :key="webhook.id">
                    <td :title="webhook.url" class="truncate-1">{{ webhook.url }}</td>
                    <td>{{ eventLabels(webhook) }}</td>
                    <td>{{ webhook.includeXml ? 'Sim' : 'Não' }}</td>
                    <td class="nowrap">
                      <span v-if="webhook.active">Ativo</span>
                      <span v-else :title="disabledTitle(webhook)">Desativado</span>
                      <small v-if="webhook.consecutiveFailures > 0" class="ml-1">
                        ({{ webhook.consecutiveFailures }} falha(s) seguida(s))
                      </small>
                    </td>
                    <td class="nowrap">{{ webhook.createdAt | datetime }}</td>
                    <td class="text-right nowrap">
                      <button class="btn btn-sm btn-outline" title="Enviar evento de teste" @click="ping(webhook)">
                        <send-icon size="1.3x"/>
                      </button>
                      <button class="btn btn-sm btn-outline ml-1" title="Entregas" @click="openDeliveries(webhook)">
                        <list-icon size="1.3x"/>
                      </button>
                      <button class="btn btn-sm btn-outline ml-1" title="Editar" @click="openForm(webhook)">
                        <edit-icon size="1.3x"/>
                      </button>
                      <button
                        class="btn btn-sm btn-outline ml-1"
                        :title="webhook.active ? 'Desativar' : 'Reativar'"
                        @click="toggleActive(webhook)"
                      >
                        <power-icon size="1.3x"/>
                      </button>
                      <button class="btn btn-sm btn-outline-red ml-1" title="Excluir" @click="remove(webhook)">
                        <trash-2-icon size="1.3x"/>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>
      </template>
    </div>

    <FormModal
      v-model="showForm"
      :webhook="currentWebhook"
      :available-events="availableEvents"
      @saved="find"
      @created="showSecret"
    />

    <SecretModal v-model="showSecretModal" :secret="secret"/>

    <DeliveriesModal v-model="showDeliveries" :webhook="currentWebhook"/>
  </div>
</template>

<script>
  import DeliveriesModal from '@/components/webhooks/DeliveriesModal'
  import FormModal from '@/components/webhooks/Modal'
  import GetAccountApps from '@/mixins/GetAccountApps'
  import ModelMapper from '@/mixins/ModelMapper'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import SecretModal from '@/components/webhooks/SecretModal'
  import { EditIcon, ListIcon, PowerIcon, SendIcon, Trash2Icon } from 'vue-feather-icons'

  export default {
    components: {
      DeliveriesModal, EditIcon, FormModal, ListIcon, Pagination, PowerIcon, SecretModal, SendIcon, Trash2Icon
    },
    computed: {
      currentAxios() {
        return this.axios.v3
      },
      webhooks() {
        return this.getModels || []
      },
      availableEvents() {
        const activeApps = this.getAccountApps.filter(app => app.active).map(app => app.name)
        return this.events.filter(event => activeApps.includes(event.app))
      },
      hasAvailableEvents() {
        return this.availableEvents.length > 0
      }
    },
    created() {
      this.loadEvents()
    },
    data() {
      return {
        currentWebhook: {},
        events: [],
        modelPath: 'webhooks',
        nameModel: 'Webhook',
        secret: '',
        showDeliveries: false,
        showForm: false,
        showSecretModal: false
      }
    },
    methods: {
      disabledTitle(webhook) {
        return webhook.disabledAt ? `Desativado em ${this.$options.filters.datetime(webhook.disabledAt)}` : ''
      },
      eventLabels(webhook) {
        return (webhook.events || []).map(event => this.eventLabel(event)).join(', ')
      },
      eventLabel(event) {
        const found = this.events.find(item => item.event === event)
        return found ? found.label : event
      },
      find() {
        this.asyncTemplateSelect()
      },
      loadEvents() {
        this.currentAxios.get('webhooks/events')
          .then(response => this.events = response.data)
          .then(() => this.hasAvailableEvents && this.find())
          .catch(e => this.$notify.addError(e))
      },
      openDeliveries(webhook) {
        this.currentWebhook = webhook
        this.showDeliveries = true
      },
      openForm(webhook) {
        this.currentWebhook = webhook ? webhook.clone() : {}
        this.showForm = true
      },
      ping(webhook) {
        this.openLoad()
        this.currentAxios.post(`webhooks/${webhook.id}/ping`)
          .then(() => this.$notify.add('Evento de teste enviado. Veja o resultado em Entregas.'))
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeLoad())
      },
      remove(webhook) {
        if (!confirm(`Excluir o webhook ${webhook.url}?\nO histórico de entregas dele também será removido.`))
          return

        this.asyncTemplateDelete(webhook, {
          after: () => {
            this.$notify.add('Webhook excluído com sucesso!')
            this.find()
          }
        })
      },
      showSecret(secret) {
        this.secret = secret
        this.showSecretModal = true
      },
      toggleActive(webhook) {
        const active = !webhook.active

        this.asyncTemplateSave({ id: webhook.id, active: active }, {
          after: () => {
            this.$notify.add(active ? 'Webhook reativado' : 'Webhook desativado')
            this.find()
          }
        })
      }
    },
    mixins: [GetAccountApps, ModelMapper],
    name: 'WebhookIndex'
  }
</script>

<style scoped>
</style>
